/** Passwordless callback policy. No Firebase imports or browser storage access. */
export const EMAIL_CALLBACK_PATH = '/auth/email/';
export const EMAIL_PENDING_KEY = 'mylocation.email-link';
export const EMAIL_PENDING_MAX_AGE = 24 * 60 * 60 * 1000;
export const EMAIL_REAUTH_INTENTS = new Set([
	'delete_cloud_data', 'rotate_invite', 'rotate_follow', 'block_friend'
]);

/** @param {unknown} input @returns {string | null} */
export function normalizeEmail(input) {
	if (typeof input !== 'string') return null;
	const email = input.trim();
	if (!email || email.length > 254 || /[\s\u0000-\u001f\u007f]/u.test(email)) return null;
	const parts = email.split('@');
	if (parts.length !== 2 || !parts[0] || !parts[1].includes('.') ||
		parts[1].startsWith('.') || parts[1].endsWith('.')) return null;
	return email;
}

/** Only locally recorded navigation is reused, never a redirect from an email URL.
 * @param {unknown} input @returns {string}
 */
export function safeAuthReturnPath(input) {
	if (typeof input !== 'string' || !input.startsWith('/') || input.startsWith('//') ||
		/[\\\u0000-\u001f\u007f]/u.test(input)) return '/app/';
	try {
		const url = new URL(input, 'https://mylocation.app');
		if (url.origin !== 'https://mylocation.app' || /^\/auth(?:\/|$)/.test(url.pathname)) return '/app/';
		// These parameters are credentials, not navigation state.
		for (const key of ['oobCode', 'apiKey', 'mode', 'link', 'continueUrl']) url.searchParams.delete(key);
		return url.pathname + url.search + url.hash;
	} catch { return '/app/'; }
}

/** @param {string} value */
function secureOrigin(value) {
	try {
		const url = new URL(value);
		const local = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
		return !url.username && !url.password && (url.protocol === 'https:' || (local && url.protocol === 'http:'));
	} catch { return false; }
}

/** @typedef {{apiKey: string, projectId: string, authDomain: string, origin: string}} EmailLinkConfig */
/**
 * Firebase can generate action links using different public app/Hosting API
 * keys within one project. The configured Auth instance redeems the one-time
 * code against its project; the link's API key is not authentication authority.
 * Reject foreign origins, explicit projects, tenants and ambiguous parameters.
 * Email is never read here.
 * @param {string} input @param {EmailLinkConfig} config
 * @returns {{link: string, purpose: 'signIn' | 'reauthenticate', requestId: string} | null}
 */
export function parseEmailCallback(input, config) {
	if (typeof input !== 'string' || input.length > 16384 || !config.apiKey ||
		!config.projectId || !config.authDomain || !secureOrigin(config.origin)) return null;
	try {
		const domain = new URL(`https://${config.authDomain}`);
		if (domain.hostname !== config.authDomain || domain.pathname !== '/' || domain.port ||
			domain.search || domain.hash || domain.username || domain.password) return null;
		if (domain.hostname.endsWith('.firebaseapp.com') &&
			domain.hostname !== `${config.projectId}.firebaseapp.com`) return null;
		const url = new URL(input);
		if (url.origin !== new URL(config.origin).origin || !secureOrigin(url.href) ||
			!['/', '/auth/email', EMAIL_CALLBACK_PATH].includes(url.pathname) || url.hash) return null;
		const params = url.searchParams;
		for (const key of ['mode', 'oobCode', 'apiKey', 'projectId', 'tenantId', 'purpose', 'state']) {
			if (params.getAll(key).length > 1) return null;
		}
		// Firebase unwraps these before extracting its action. This callback is
		// deliberately direct-only, so never pass an unvalidated nested action.
		if (params.has('link') || params.has('deep_link_id')) return null;
		// Hosting, native and web apps can use distinct public API keys. Firebase
		// redeems only email/oobCode using our configured Auth instance, so a
		// foreign project's code cannot authenticate against this project.
		const validKey = !!params.get('apiKey');
		if (params.get('mode') !== 'signIn' || !params.get('oobCode') ||
			!validKey || params.has('tenantId') ||
			(params.has('projectId') && params.get('projectId') !== config.projectId)) return null;
		const purpose = params.get('purpose');
		if (purpose && purpose !== 'reauthenticate') return null;
		return { link: url.href, purpose: purpose ? 'reauthenticate' : 'signIn', requestId: params.get('state') || '' };
	} catch { return null; }
}

/** @typedef {{email: string, purpose: 'signIn' | 'reauthenticate', uid: string, intent: string | null, requestId: string, returnTo: string, sentAt: number}} PendingEmailLink */
/** @param {unknown} text @param {number} [now] @returns {PendingEmailLink | null} */
export function readPendingEmailLink(text, now = Date.now()) {
	if (typeof text !== 'string') return null;
	try {
		const pending = JSON.parse(text);
		const email = normalizeEmail(pending.email);
		const age = now - pending.sentAt;
		if (!email || !Number.isFinite(pending.sentAt) || age < 0 || age >= EMAIL_PENDING_MAX_AGE ||
			!['signIn', 'reauthenticate'].includes(pending.purpose) ||
			typeof pending.requestId !== 'string' || !pending.requestId ||
			typeof pending.uid !== 'string' ||
			(pending.purpose === 'reauthenticate' && !pending.uid) ||
			(pending.purpose === 'signIn' && pending.uid) ||
			(pending.intent !== null && !EMAIL_REAUTH_INTENTS.has(pending.intent))) return null;
		return { ...pending, email, returnTo: safeAuthReturnPath(pending.returnTo) };
	} catch { return null; }
}

/** @param {PendingEmailLink | null} pending @param {{requestId: string, purpose: string}} callback */
export function pendingMatchesCallback(pending, callback) {
	return !!pending && !!callback.requestId && pending.requestId === callback.requestId &&
		pending.purpose === callback.purpose;
}
